import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  FileType,
  InfringementLabel,
  ReportStatus,
  type File as GqlFile,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']
type ListAsset = NonNullable<InfringementNode['infringingFile']['asset']>

type UploadedProcessingReport = {
  copyright: Pick<GqlFile, 'id' | 'name' | 'type' | 'asset'>
  infringingFile: {
    id: string
    name: string
    type: FileType
  }
}

const assetTypename = (type: FileType): ListAsset['__typename'] => {
  if (type === FileType.Audio || type === FileType.Video) return 'StreamAsset'
  if (type === FileType.Pdf || type === FileType.Text) return 'DocumentAsset'
  return 'ImageAsset'
}

export const createAsset = (
  type: FileType,
  previewImageUrl?: string | null
): ListAsset => ({
  __typename: assetTypename(type),
  previewImageUrl: previewImageUrl ?? '',
})

const sameSubject = (a: InfringementNode, b: InfringementNode) =>
  a.id === b.id ||
  (a.infringingFile.id === b.infringingFile.id &&
    a.copyrights.some((copyright) =>
      b.copyrights.some((other) => other.id === copyright.id)
    ))

const subjectKey = (infringement: InfringementNode) =>
  `${infringement.infringingFile.id}:${infringement.copyrights
    .map((copyright) => copyright.id)
    .sort()
    .join(',')}`

const isServerReport = (infringement: InfringementNode) =>
  infringement.label === InfringementLabel.Report && !!infringement.report

export const useInfringementProcessingStore = defineStore(
  'infringementProcessing',
  () => {
    const items = ref<InfringementNode[]>([])
    const replacedFlagKeys = ref<string[]>([])

    const markFlagReplacement = (item: InfringementNode) => {
      const key = subjectKey(item)
      if (!replacedFlagKeys.value.includes(key)) {
        replacedFlagKeys.value.push(key)
      }
    }

    const upsert = (item: InfringementNode, replacesFlag = false) => {
      const index = items.value.findIndex((existing) =>
        sameSubject(existing, item)
      )

      if (index === -1) {
        items.value.unshift(item)
        if (replacesFlag) markFlagReplacement(item)
        return
      }

      items.value.splice(index, 1, item)
      if (replacesFlag) markFlagReplacement(item)
    }

    const addUploadedProcessingReport = ({
      copyright,
      infringingFile,
    }: UploadedProcessingReport) => {
      const id = `processing:${copyright.id}:${infringingFile.id}`

      upsert({
        __typename: 'Infringement',
        id,
        label: InfringementLabel.Report,
        timestamp: new Date().toISOString(),
        copyrights: [
          {
            __typename: 'File',
            id: copyright.id,
            name: copyright.name,
            type: copyright.type,
            asset: copyright.asset
              ? createAsset(copyright.type, copyright.asset.previewImageUrl)
              : null,
          },
        ],
        infringingFile: {
          __typename: 'InfringingFile',
          id: infringingFile.id,
          name: infringingFile.name,
          type: infringingFile.type,
          asset: createAsset(infringingFile.type),
        },
        matches: null,
        report: {
          __typename: 'Report',
          id,
          status: ReportStatus.Processing,
        },
      })
    }

    const addExistingProcessingReport = (infringement: InfringementNode) => {
      const id = `processing:${infringement.id}`

      upsert(
        {
          ...infringement,
          label: InfringementLabel.Report,
          report: {
            __typename: 'Report',
            id,
            status: ReportStatus.Processing,
          },
        },
        infringement.label === InfringementLabel.Flag
      )
    }

    const merge = (infringements: InfringementNode[]) => {
      const additions = items.value.filter(
        (item) =>
          !infringements.some((infringement) => sameSubject(infringement, item))
      )

      const merged = infringements.map((infringement) => {
        if (isServerReport(infringement)) return infringement
        return (
          items.value.find((item) => sameSubject(infringement, item)) ??
          infringement
        )
      })

      return [...additions, ...merged]
    }

    const withoutPendingReports = (infringements: InfringementNode[]) =>
      infringements.filter(
        (infringement) =>
          !items.value.some((item) => sameSubject(infringement, item))
      )

    const reconcile = (infringements: InfringementNode[]) => {
      const settled = items.value.filter((item) =>
        infringements.some(
          (infringement) =>
            sameSubject(infringement, item) && isServerReport(infringement)
        )
      )

      if (!settled.length) return

      const settledKeys = settled.map(subjectKey)

      items.value = items.value.filter((item) => !settled.includes(item))
      replacedFlagKeys.value = replacedFlagKeys.value.filter(
        (key) => !settledKeys.includes(key)
      )
    }

    const pendingReportCount = computed(() => items.value.length)

    const pendingFlagReplacementCount = computed(
      () =>
        items.value.filter((item) =>
          replacedFlagKeys.value.includes(subjectKey(item))
        ).length
    )

    return {
      items,
      addUploadedProcessingReport,
      addExistingProcessingReport,
      merge,
      withoutPendingReports,
      reconcile,
      pendingReportCount,
      pendingFlagReplacementCount,
    }
  }
)
